import { Typography } from '@euler/app/components/typography';
import { ApdmUploadButton } from '@euler/app/flows/apdm/imports/ApdmUploadButton';
import {
  kApdmBlockReasonLabels,
  kApdmCampaignKindLabels,
} from '@euler/functions/apdm/campaignLabels';
import { canManageApdmImports } from '@euler/functions/apdmAccess';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { ApdmBuildCardKind, ApdmBuildStep } from '@euler/model/apdm';
import dayjs from 'dayjs';
import {
  LucideIcon,
  SlidersHorizontal,
  Store,
  Users,
} from 'lucide-react-native';
import { memo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useAiChatActions, useAiPlanStepOpen } from '../chat/AiChatActions';
import {
  AudienceCounts,
  CardLoading,
  CardNotice,
  ChatCard,
} from '../components/ChatCard';
import { PrimaryButton } from '../components/PrimaryButton';
import { aiColors } from '../theme';
import { PlanAudienceListSheet } from './PlanAudienceListSheet';
import { Row, Section, sampleFacts } from './PlanCampaignCard';
import { isAiMessageId, usePlanCard } from './usePlanCard';

/**
 * Who the plan reaches, as its reply counted it: settle it, keep only the
 * store's records when it counts sheets beside them, add a sheet, or use
 * a new sheet instead; or adjust who it picks (spec §11.4.2).
 */
export const PlanAudienceCard = memo(
  ({ messageId, streaming }: { messageId?: string; streaming?: boolean }) => {
    const profile = useUserProfileInfo();
    if (streaming || !isAiMessageId(messageId)) return null;
    if (!canManageApdmImports(profile ?? null))
      return <CardNotice text="当前账号无法查看这些客户。" />;
    return <AudienceBody key={messageId} messageId={messageId} />;
  },
);

function AudienceBody({ messageId }: { messageId: string }) {
  const { rpx } = useResponsiveContext();
  const chat = useAiChatActions();
  const interactive = useAiPlanStepOpen(ApdmBuildStep.Who);
  const state = usePlanCard(messageId, ApdmBuildCardKind.Audience);
  const [reasons, setReasons] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  if (state.kind === 'loading')
    return (
      <ChatCard icon={Users} title="目标客户" tinted>
        <CardLoading label="正在读取客户…" />
      </ChatCard>
    );
  if (
    state.kind === 'missing' ||
    state.view.card !== ApdmBuildCardKind.Audience
  )
    return (
      <ChatCard icon={Users} title="目标客户">
        <CardNotice text="没有读取到这批客户，请稍后重新打开对话。" />
      </ChatCard>
    );
  const card = state.view.audience;
  const counts = card.counts;
  const kinds = new Set(card.audience.sources.map(source => source.kind));
  // A query picks its customers from the store's records alone.
  const merged =
    card.audience.rule.kind !== 'query' &&
    kinds.has('platform') &&
    (kinds.has('shop_imports') || kinds.has('imports'));
  return (
    <ChatCard
      icon={Users}
      tinted
      title="目标客户"
      subtitle={kApdmCampaignKindLabels[card.kind]}
    >
      <AudienceCounts counts={counts} />
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
          {dayjs(card.asOf).format('M月D日 HH:mm')} 统计
        </Typography.Text>
        {counts.exclusionReasons.length > 0 && (
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: reasons }}
            hitSlop={6}
            onPress={() => setReasons(value => !value)}
          >
            <Typography.Text medium size={rpx(12)} color={aiColors.primary}>
              {reasons ? '收起排除原因' : '查看排除原因'}
            </Typography.Text>
          </Pressable>
        )}
      </View>
      {reasons &&
        counts.exclusionReasons.map(entry => (
          <Row
            key={entry.reason}
            label={kApdmBlockReasonLabels[entry.reason]}
            value={`${entry.count}`}
          />
        ))}
      {card.samples.length > 0 && (
        <Section title="客户示例">
          {card.samples.map(sample => (
            <Row
              key={sample.licensePlateNo}
              label={sample.licensePlateNo}
              value={sampleFacts(sample.facts)}
            />
          ))}
        </Section>
      )}
      {counts.total > 0 && (
        <>
          <Pressable
            accessibilityRole="button"
            hitSlop={6}
            onPress={() => setListOpen(true)}
            style={{ alignSelf: 'flex-start' }}
          >
            <Typography.Text medium size={rpx(12)} color={aiColors.primary}>
              查看全部 {counts.total} 位客户
            </Typography.Text>
          </Pressable>
          <PlanAudienceListSheet
            messageId={messageId}
            visible={listOpen}
            onClose={() => setListOpen(false)}
          />
        </>
      )}
      {counts.known + counts.unconfirmed === 0 && (
        <CardNotice text="还没有符合条件的客户。可以换一份表格，或换个条件。" />
      )}
      {interactive ? (
        <>
          <PrimaryButton
            title="这批客户可以"
            disabled={counts.known + counts.unconfirmed === 0}
            onPress={() =>
              chat.send('这批客户可以', {
                answer: { step: ApdmBuildStep.Who },
              })
            }
          />
          <View style={{ flexDirection: 'row', gap: rpx(8) }}>
            {merged ? (
              <CardAction
                label="只用门店的客户记录"
                icon={Store}
                onPress={() => chat.send('只用门店的客户记录')}
              />
            ) : null}
            <CardAction
              label="调整客户范围"
              icon={SlidersHorizontal}
              onPress={() => chat.send('我想调整一下客户范围')}
            />
          </View>
          <View style={{ flexDirection: 'row', gap: rpx(8) }}>
            <View style={{ flex: 1 }}>
              <ApdmUploadButton
                appearance="row"
                label="追加表格"
                icon="file_plus"
                onUploaded={async info => {
                  chat.send('追加这份客户表格', { attachments: [info] });
                }}
              />
            </View>
            <View style={{ flex: 1 }}>
              <ApdmUploadButton
                appearance="row"
                label="换成新表格"
                icon="replace"
                onUploaded={async info => {
                  chat.send('只用这份新表格', { attachments: [info] });
                }}
              />
            </View>
          </View>
        </>
      ) : null}
    </ChatCard>
  );
}

function CardAction({
  label,
  icon: Icon,
  onPress,
}: {
  label: string;
  icon: LucideIcon;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => ({
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        height: 32,
        paddingHorizontal: 10,
        borderWidth: 1,
        borderColor: aiColors.border,
        borderRadius: 9,
        backgroundColor: aiColors.card,
        opacity: pressed ? 0.7 : 1,
      })}
    >
      <Icon size={16} color={aiColors.primary} />
      <Typography.Text size={13} color={aiColors.foreground}>
        {label}
      </Typography.Text>
    </Pressable>
  );
}
